import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { UserCircle, LockKey, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlockedClientsSection = ({ userId, userRole }) => {
  const [blockedClients, setBlockedClients] = useState(() => readInstantCache('blocked_clients', 'all') || []);
  const [loading, setLoading] = useState(() => !readInstantCache('blocked_clients', 'all'));

  useEffect(() => {
    loadBlockedClients();
  }, [userId]);

  const loadBlockedClients = async () => {
    try {
      if (!readInstantCache('blocked_clients', 'all')) setLoading(true);
      const response = await axios.get(`${API}/clients/blocked`, {
        params: { user_id: userId, role: userRole }
      });
      setBlockedClients(response.data);
      writeInstantCache('blocked_clients', 'all', response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load blocked clients:', error);
      toast.error('Failed to load blocked clients');
      setLoading(false);
    }
  };

  const handleUnblock = async (customerId, customerName) => {
    if (!window.confirm(`Unblock ${customerName}'s account?`)) return;

    try {
      await axios.post(`${API}/clients/unblock/${customerId}`);
      toast.success(`${customerName} unblocked successfully!`);
      loadBlockedClients(); // Reload list
    } catch (error) {
      console.error('Failed to unblock:', error);
      toast.error('Failed to unblock client');
    }
  };

  const handleUnblockDevice = async (customerId, customerName) => {
    if (!window.confirm(`Unblock ${customerName}'s DEVICE + IP?\n\nThis will remove every device fingerprint / IP that was blacklisted for this customer, allowing them to log in from that browser / phone again.`)) return;
    try {
      const r = await axios.post(`${API}/customers/unblock-device/${customerId}`);
      toast.success(`Device unblocked (${r.data?.removed || 0} entries removed)`);
      loadBlockedClients();
    } catch (error) {
      console.error('Failed to unblock device:', error);
      toast.error('Failed to unblock device');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-gray-500">Loading blocked clients...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <LockKey size={24} weight="bold" className="text-red-600" />
        <h2 className="text-lg font-bold text-gray-800">Blocked Clients</h2>
      </div>

      {blockedClients.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <LockKey size={48} weight="light" className="mx-auto mb-3 text-gray-300" />
          <p>No blocked clients</p>
        </div>
      ) : (
        <div className="space-y-3">
          {blockedClients.map((client) => (
            <div
              key={client.id}
              className="flex items-center gap-3 p-4 border border-red-100 rounded-lg bg-red-50 hover:bg-red-100 transition-colors"
              data-testid={`blocked-client-${client.id}`}
            >
              <UserCircle size={40} weight="fill" className="text-gray-400 flex-shrink-0" />
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="font-semibold text-gray-800">{client.username || 'Unnamed'}</p>
                  {client.block_mode === 'device' || client.device_block_count > 0 ? (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-red-600 text-white">🚫 Device/IP</span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-orange-500 text-white">Account</span>
                  )}
                  {client.device_block_count > 0 && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-red-100 text-red-700 rounded">{client.device_block_count} fp/ip</span>
                  )}
                </div>
                <p className="text-sm text-gray-600">{client.phone}</p>
                {client.block_reason && (
                  <p className="text-xs text-red-600 mt-1">Reason: {client.block_reason}</p>
                )}
                {client.blocked_by && (
                  <p className="text-[11px] text-gray-500">By: {client.blocked_by} ({client.blocked_by_role || '-'})</p>
                )}
                <p className="text-xs text-gray-500">
                  Blocked: {client.blocked_at ? new Date(client.blocked_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}
                </p>
              </div>

              <div className="flex flex-col gap-1.5 flex-shrink-0">
                <button
                  onClick={() => handleUnblock(client.id, client.username || client.phone)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 text-xs"
                  data-testid={`unblock-account-${client.id}`}
                >
                  <CheckCircle size={14} weight="bold" />
                  <span className="font-medium">Unblock Account</span>
                </button>
                {client.device_block_count > 0 && (
                  <button
                    onClick={() => handleUnblockDevice(client.id, client.username || client.phone)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
                    data-testid={`unblock-device-${client.id}`}
                  >
                    <CheckCircle size={14} weight="bold" />
                    <span className="font-medium">Unblock Device/IP</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlockedClientsSection;
